import { useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import StockDataTable, { TableColumn } from "react-data-table-component";
import styled from "styled-components";
import { remove as removeAccents } from "remove-accents-diacritics";
import { CSVLink } from "react-csv";
import moment from "moment";

const TextField = styled.input`
	height: 32px;
	width: 200px;
	border-radius: 3px;
	border-top-left-radius: 5px;
	border-bottom-left-radius: 5px;
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
	border: 1px solid #e5e5e5;
	padding: 0 32px 0 16px;

	&:hover {
		cursor: pointer;
	}
`;

const ClearButton = styled(Button)`
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
	border-top-right-radius: 5px;
	border-bottom-right-radius: 5px;
	height: 32px;
	width: 32px;
	text-align: center;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const CSVButton = styled(Button)`
	margin-right: 4px;
	height: 32px;
	text-align: center;
`;

const FilterComponent = ({
	filterText,
	onFilter,
	onClear
}: {
	filterText: string;
	onFilter: (e: any) => void;
	onClear: (e: any) => void;
}) => (
	<>
		<TextField
			id="search"
			type="text"
			placeholder="Search..."
			aria-label="Search Input"
			value={filterText}
			onChange={onFilter}
		/>
		<ClearButton type="button" onClick={onClear}>
			X
		</ClearButton>
	</>
);

const CSVDownload = ({
	csvName,
	data = []
}: {
	csvName?: string;
	data: any[];
}) => {
	return (
		<CSVLink
			filename={`${moment(new Date()).format("YYYYMMDDHHmm")}-${csvName}.csv`}
			data={data}
		>
			<CSVButton variant="primary" onClick={() => {}}>
				⇩ CSV
			</CSVButton>
		</CSVLink>
	);
};

export const DataTable = ({
	columns,
	data,
	pagination,
	selectableRows,
	persistTableHead,
	title,
	filterEnabled,
	csvDownload: {
		enabled: csvDownloadEnabled,
		fileName: csvDownloadFileName,
		converter: csvDownloadConverter
	} = {},
	customHeaderComponents = [],
	onSelectedRowsChange
}: {
	columns: TableColumn<any>[];
	data: any[];
	pagination?: boolean;
	selectableRows?: boolean;
	persistTableHead?: boolean;
	title?: React.ReactNode;
	filterEnabled?: boolean;
	csvDownload?: {
		enabled?: boolean;
		fileName?: string;
		converter?: (data: any[]) => any[];
	};
	customHeaderComponents?: React.ReactNode[];
	onSelectedRowsChange?: (selected: {
		allSelected: boolean;
		selectedCount: number;
		selectedRows: any[];
	}) => void;
}) => {
	const [filterText, setFilterText] = useState("");
	const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
	const filteredItems = data.filter(
		(item) =>
			item.fullText &&
			item.fullText.includes(removeAccents(filterText.toLowerCase()))
	);

	const filterMemo = useMemo(() => {
		const handleClear = () => {
			if (filterText) {
				setResetPaginationToggle(!resetPaginationToggle);
				setFilterText("");
			}
		};

		return (
			<FilterComponent
				onFilter={(e) => setFilterText(e.target.value)}
				onClear={handleClear}
				filterText={filterText}
			/>
		);
	}, [filterText, resetPaginationToggle]);

	const csvDownloadMemo = useMemo(() => {
		const csvData = csvDownloadConverter ? csvDownloadConverter(data) : data;
		return <CSVDownload csvName={csvDownloadFileName} data={csvData} />;
	}, [data, csvDownloadFileName, csvDownloadConverter]);

	const headerComponents = useMemo(() => {
		let components = [];
		if (csvDownloadEnabled) {
			components.push(csvDownloadMemo);
		}
		if (filterEnabled) {
			components.push(filterMemo);
		}
		components.unshift(...customHeaderComponents);
		return components;
	}, [
		filterEnabled,
		csvDownloadEnabled,
		filterMemo,
		csvDownloadMemo,
		customHeaderComponents
	]);

	return (
		<StockDataTable
			title={title}
			columns={columns}
			data={filteredItems}
			pagination={pagination}
			paginationResetDefaultPage={resetPaginationToggle} // optionally, a hook to reset pagination to page 1
			subHeader
			subHeaderComponent={headerComponents}
			persistTableHead={persistTableHead}
			selectableRows={selectableRows}
			selectableRowsHighlight
			onSelectedRowsChange={onSelectedRowsChange}
		/>
	);
};
